import * as Yup from 'yup';
import { CodedException, AppSettings } from '@app/base';
import {
  PROVIDER_INFO_INITIAL_VALUE,
  ProviderInfoFieldSet,
  PROVIDER_INFO_SCHEMA,
} from '@app/main/widgets/Employees.Details';
import { SIGN_UP_INITIAL_VALUE, SignUpInfoFieldSet } from '@app/main/widgets/SignUp.Common';
import { Form } from '@webinex/antik';
import { useMemo } from 'react';
import { FormHCaptcha } from '@app/common/components';
import { useTranslation } from 'react-i18next';

function useSchema() {
  return useMemo(() => {
    const captcha = AppSettings.hCaptcha.enabled
      ? Yup.string().nullable().required()
      : Yup.string().nullable().defined();

    return Yup.object({
      email: Yup.string().email().field().nullable().required(),
      firstName: Yup.string().field().nullable().required(),
      lastName: Yup.string().field().nullable().required(),
      password: Yup.string().password().nullable().required(),
      passwordConfirmation: Yup.string()
        .nullable()
        .oneOf([Yup.ref('password'), null], { key: 'signup.passwordConfirmationMatchError' })
        .required(),
      providerInfo: PROVIDER_INFO_SCHEMA.nullable().required(),
      captcha,
    });
  }, []);
}

export type ProviderSignUpFormValue = Yup.InferType<ReturnType<typeof useSchema>>;

export interface ProviderSignUpFormProps {
  onSubmit: (value: ProviderSignUpFormValue) => any;
  error?: CodedException;
}

const INITIAL_VALUE: ProviderSignUpFormValue = {
  ...SIGN_UP_INITIAL_VALUE,
  providerInfo: PROVIDER_INFO_INITIAL_VALUE,
  captcha: null,
};

export function ProviderSignUpForm({ onSubmit }: ProviderSignUpFormProps) {
  const schema = useSchema();
  const { t } = useTranslation();

  return (
    <Form
      uid="signup"
      type="formik"
      ns="employees.signup"
      initialValues={INITIAL_VALUE}
      onSubmit={onSubmit}
      validationSchema={schema}
    >
      <SignUpInfoFieldSet />
      <ProviderInfoFieldSet name="providerInfo" />
      {AppSettings.hCaptcha.enabled && (
        <Form.Item className="text-center" name="captcha" required label={false}>
          {AppSettings.hCaptcha.enabled && <FormHCaptcha name="captcha" />}
        </Form.Item>
      )}
      <input type="submit" style={{ display: 'none' }} aria-label={t('save')} />
    </Form>
  );
}
